
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import Header from '@/components/Header';
import Sidebar from '@/components/Sidebar';
import Toolbar from '@/components/Toolbar';
import VideoPreview from '@/components/VideoPreview';
import Timeline from '@/components/Timeline';
import AIPanel from '@/components/AIPanel';
import { Project } from '@/hooks/useProjects';
import { useToast } from '@/components/ui/use-toast';

const ProjectEditor = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [exportStatus, setExportStatus] = useState<string | null>(null);
  const [exportStatusMessage, setExportStatusMessage] = useState<string | null>(null);

  const { data: project, isLoading, error: projectError } = useQuery({
    queryKey: ['project', projectId],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID is required');
      
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', projectId)
        .single();

      if (error) throw error;
      return data as Project;
    },
    enabled: !!projectId,
  });

  // Handle project data changes
  useEffect(() => {
    if (project) {
      setExportStatus(project.export_status);
      if (project.export_status === 'failed') {
        setExportStatusMessage(project.export_last_error || 'Export failed. Please try again.');
      } else if (project.export_status === 'completed') {
        setExportStatusMessage('Video is ready for download.');
      } else {
        setExportStatusMessage(null);
      }
    }
  }, [project]);

  useEffect(() => {
    if (!projectId) return;

    const channel = supabase
      .channel(`project-updates:${projectId}`)
      .on<Project>(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'projects', filter: `id=eq.${projectId}` },
        (payload) => {
          console.log('Project updated via realtime:', payload.new);
          queryClient.setQueryData(['project', projectId], payload.new);
          setExportStatus(payload.new.export_status);
          if (payload.new.export_status === 'completed') {
            setExportStatusMessage('Video is ready for download.');
            toast({
              title: "Export Complete",
              description: "Your video is now available for download.",
              variant: "default",
            });
          } else if (payload.new.export_status === 'failed') {
            setExportStatusMessage(payload.new.export_last_error || 'Export failed unexpectedly.');
            toast({
              title: "Export Failed",
              description: payload.new.export_last_error || 'An unknown error occurred.',
              variant: "destructive",
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [projectId, queryClient, toast]);

  const exportMutation = useMutation({
    mutationFn: async () => {
      if (!projectId) throw new Error("Project ID not found");
      const { data, error } = await supabase.functions.invoke('request-project-video-export', {
        body: { projectId },
      });

      if (error) throw error;
      return data;
    },
    onMutate: () => {
      setExportStatus('queued');
      setExportStatusMessage('Sending request to the export queue...');
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      setExportStatusMessage(data.message || 'Export queued successfully!');
      toast({
        title: "Export Queued",
        description: "Your video export has started. We'll notify you when it's ready.",
      });
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error?.message || error.message;
      setExportStatus('failed');
      setExportStatusMessage(errorMessage);
      toast({
        title: "Error Starting Export",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const handleExport = () => {
    exportMutation.mutate();
  };

  const handleDownload = async () => {
    if (!project?.exported_video_storage_path) return;

    const { data, error } = await supabase.storage
      .from('exported-videos')
      .createSignedUrl(project.exported_video_storage_path, 60); // 60 seconds validity

    if (error) {
      toast({ title: "Download Error", description: "Could not create download link.", variant: "destructive" });
      return;
    }
    window.open(data.signedUrl, '_blank');
  };

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-editor-dark">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-editor-accent"></div>
      </div>
    );
  }

  if (projectError || !project) {
    return (
      <div className="h-screen flex items-center justify-center bg-editor-dark">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-white mb-2">Project not found</h2>
          <p className="text-gray-400">The project you're looking for doesn't exist or you don't have access to it.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-editor-dark">
      <Header
        project={project}
        exportStatus={exportStatus}
        exportStatusMessage={exportStatusMessage}
        isExporting={exportMutation.isPending}
        onExport={handleExport}
        onDownload={handleDownload}
      />
      
      <div className="flex-1 flex overflow-hidden">
        <Sidebar projectId={projectId} />
        
        <div className="flex-1 flex flex-col overflow-hidden">
          <Toolbar />
          <div className="flex-1 flex overflow-hidden">
            <VideoPreview project={project} />
            <AIPanel project={project} />
          </div>
          <Timeline project={project} />
        </div>
      </div>
    </div>
  );
};

export default ProjectEditor;
